import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Group as MantineGroup,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconCalendarEvent,
  IconChevronRight,
  IconDots,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { formatPractitionerPreferredLabel } from '@unify/fhir-model/resources/practitioner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { fetchAdminDirectory } from '@/lib/adminDirectoryService';
import {
  canEditGroupSetup,
  canEnrollPatientInGroup,
  canManageGroupRoster,
  enrollPatientInGroup,
  facilitatorReferencesFromForm,
  findExistingGroupEnrollmentEpisodeOfCare,
  getActiveGroupMemberPatientIds,
  getEpisodeOfCareGroupRef,
  groupListItemFromResource,
  GROUP_LIFECYCLE_STATUS_COLORS,
  GROUP_LIFECYCLE_STATUS_LABELS,
  isGroupEnrollmentLocked,
  joinGroupWithTemplate,
  listGroupFacilitatorOptions,
  readTherapyGroup,
  requestGroupAccessSync,
  requestAppointmentSeriesSyncParticipants,
  returnPatientToGroup,
  unenrollPatientFromGroup,
  type GroupParticipationEndMode,
} from '@/lib/groupTherapy';
import {
  getGroupFacilitators,
  getManagingOrganizationId,
} from '@unify/fhir-model/resources/group';
import { useEffectiveDisplayUserRole } from '@/lib/displayUserRole';
import type { Appointment, EpisodeOfCare, Group } from '@medplum/fhirtypes';
import type { Patient } from '@medplum/fhirtypes';
import { THERAPY_SERVICE_TYPE_SYSTEM } from '@unify/fhir-model/constants';
import { searchGroupTemplates, type GroupTemplateListItem } from '@/lib/groupTherapy';

import { GroupAvatar } from './components/GroupAvatar';
import { GroupSetupFormModal } from './components/GroupSetupFormModal';
import { useGroupTherapyGroups } from './hooks/useGroupTherapyGroups';

type RosterRow = {
  patientId: string;
  patientName: string;
  enrolledDate?: string;
  status: 'active' | 'onhold';
};

function patientDisplayName(patient: Patient): string {
  return (
    patient.name?.[0]?.text?.trim() ??
    [patient.name?.[0]?.given?.join(' '), patient.name?.[0]?.family].filter(Boolean).join(' ') ??
    patient.id ??
    'Unknown'
  );
}

function episodeLinksGroup(eoc: EpisodeOfCare, groupId: string): boolean {
  if (getEpisodeOfCareGroupRef(eoc)?.reference !== `Group/${groupId}`) return false;
  return (
    eoc.type?.some((t) =>
      t.coding?.some(
        (c) =>
          c.system === THERAPY_SERVICE_TYPE_SYSTEM &&
          (c.code === 'group' || c.code === 'group-therapy'),
      ),
    ) ?? false
  );
}

function sessionDateLabel(start: string | undefined): string {
  if (!start) return 'Unscheduled session';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(start));
}

function sessionTimeLabel(appointment: Appointment): string {
  if (!appointment.start) return 'Time not scheduled';
  const fmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
  const start = fmt.format(new Date(appointment.start));
  return appointment.end ? `${start} – ${fmt.format(new Date(appointment.end))}` : start;
}

export function GroupDetailPage(): JSX.Element {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const userRole = useEffectiveDisplayUserRole();
  const canEditSetup = canEditGroupSetup(userRole);
  const { saveGroup } = useGroupTherapyGroups();

  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState<Group | null>(null);
  const [cohort, setCohort] = useState<ReturnType<typeof groupListItemFromResource> | null>(null);
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [sessions, setSessions] = useState<Appointment[]>([]);
  const [patientOptions, setPatientOptions] = useState<{ value: string; label: string }[]>([]);
  const [providers, setProviders] = useState<
    { value: string; label: string; preferredLabel?: string; practiceId: string }[]
  >(
    [],
  );
  const [templates, setTemplates] = useState<GroupTemplateListItem[]>([]);
  const [selectedPatientIds, setSelectedPatientIds] = useState<string[]>([]);
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [busy, setBusy] = useState(false);
  const [savingSetup, setSavingSetup] = useState(false);

  // Image, description, and screening config live on the template, not the cohort Group.
  const summary = useMemo(
    () =>
      cohort
        ? joinGroupWithTemplate(
            cohort,
            templates.find((t) => t.reference === cohort.templateReference),
          )
        : null,
    [cohort, templates],
  );

  const canManageRoster = group
    ? canManageGroupRoster(userRole, group, profile?.id)
    : canEditSetup;

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const resource = await readTherapyGroup(medplum, groupId);
      if (!resource) {
        setGroup(null);
        setCohort(null);
        setRoster([]);
        setSessions([]);
        return;
      }
      setGroup(resource);
      setCohort(groupListItemFromResource(resource));

      const memberIds = getActiveGroupMemberPatientIds(resource);
      const patients = await Promise.all(
        memberIds.map(async (id) => {
          try {
            return await medplum.readResource('Patient', id);
          } catch {
            return { resourceType: 'Patient' as const, id, name: [{ text: id }] };
          }
        }),
      );
      const activeRows: RosterRow[] = patients.map((patient, idx) => {
        const member = resource.member?.find(
          (m) => m.entity?.reference === `Patient/${memberIds[idx]}`,
        );
        return {
          patientId: memberIds[idx],
          patientName: patientDisplayName(patient),
          enrolledDate: member?.period?.start,
          status: 'active',
        };
      });

      let onHoldRows: RosterRow[] = [];
      try {
        const onHoldEocs = await medplum.searchResources('EpisodeOfCare', {
          status: 'onhold',
          _count: '100',
        });
        const activeSet = new Set(memberIds);
        const mapped: Array<RosterRow | null> = await Promise.all(
          onHoldEocs.filter((eoc) => episodeLinksGroup(eoc, groupId)).map(async (eoc) => {
            const pid = eoc.patient?.reference?.replace(/^Patient\//, '');
            if (!pid || activeSet.has(pid)) return null;
            try {
              const patient = await medplum.readResource('Patient', pid);
              return {
                patientId: pid,
                patientName: patientDisplayName(patient),
                enrolledDate: eoc.period?.start,
                status: 'onhold' as const,
              };
            } catch {
              return {
                patientId: pid,
                patientName: pid,
                enrolledDate: eoc.period?.start,
                status: 'onhold' as const,
              };
            }
          }),
        );
        onHoldRows = mapped.filter((r): r is RosterRow => r != null);
      } catch {
        onHoldRows = [];
      }

      setRoster([...activeRows, ...onHoldRows]);

      try {
        // The series bot writes appointments server-side, so the client's cached search
        // would otherwise keep serving the pre-save sessions after a schedule change.
        medplum.invalidateSearches('Appointment');
        const appts = await medplum.searchResources('Appointment', {
          'supporting-info': `Group/${groupId}`,
          'status:not': 'cancelled',
          _sort: 'date',
          _count: '100',
        });
        setSessions(appts);
      } catch {
        setSessions([]);
      }

      const directory = await fetchAdminDirectory(medplum);
      const orgId = getManagingOrganizationId(resource);
      const orgPatientIds = orgId ? (directory.clientIdsByOrganizationId[orgId] ?? []) : [];
      const enrolled = new Set(memberIds);
      const options: { value: string; label: string }[] = [];
      for (const patientId of orgPatientIds) {
        if (enrolled.has(patientId)) continue;
        try {
          const patient = await medplum.readResource('Patient', patientId);
          options.push({ value: patientId, label: patientDisplayName(patient) });
        } catch {
          options.push({ value: patientId, label: patientId });
        }
      }
      setPatientOptions(options);
      setProviders(await listGroupFacilitatorOptions(medplum));
      setTemplates(await searchGroupTemplates(medplum, orgId));
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Could not load group',
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [groupId, medplum]);

  useEffect(() => {
    void load();
  }, [load]);

  const enrollmentLocked = useMemo(() => {
    if (!group) return false;
    return isGroupEnrollmentLocked(group);
  }, [group]);

  const activeMemberCount = useMemo(
    () => roster.filter((r) => r.status === 'active').length,
    [roster],
  );

  const remainingCapacity = useMemo(() => {
    if (!summary) return 0;
    return Math.max(0, summary.maxEnrollment - activeMemberCount);
  }, [activeMemberCount, summary]);

  const canAddMember = useMemo(() => {
    if (!summary || !canManageRoster) return false;
    return canEnrollPatientInGroup(
      summary.enrollmentMode,
      enrollmentLocked,
      activeMemberCount,
      summary.maxEnrollment,
    );
  }, [activeMemberCount, canManageRoster, enrollmentLocked, summary]);

  const openAddMembers = useCallback(() => {
    setSelectedPatientIds([]);
    openAdd();
  }, [openAdd]);

  const syncAccessAndParticipants = useCallback(
    async (input: {
      action: 'enroll' | 'unenroll' | 'facilitators' | 'discharge';
      patientId?: string;
      careTeamId?: string;
    }) => {
      if (!groupId) return;
      try {
        await requestGroupAccessSync(medplum, { groupId, ...input });
        await requestAppointmentSeriesSyncParticipants(medplum, groupId);
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);
        notifications.show({
          color: 'yellow',
          title: 'Access sync incomplete',
          message: `Membership updated, but group access/participants sync failed: ${detail}`,
        });
      }
    },
    [groupId, medplum],
  );

  const handleAddMembers = async () => {
    if (!groupId || selectedPatientIds.length === 0 || !summary || !group) return;
    if (selectedPatientIds.length > remainingCapacity) {
      notifications.show({
        color: 'red',
        title: 'Enrollment failed',
        message: `Only ${remainingCapacity} seat${remainingCapacity === 1 ? '' : 's'} remaining.`,
      });
      return;
    }
    setBusy(true);
    try {
      const facilitators = getGroupFacilitators(group);
      const facilitatorIds = facilitators
        .map((r) => r.reference?.replace(/^Practitioner\//, ''))
        .filter((id): id is string => !!id);
      const facilitatorDisplays = facilitatorIds.map(
        (id) => providers.find((provider) => provider.value === id)?.preferredLabel ?? '',
      );
      const organizationId = getManagingOrganizationId(group);

      let currentGroup = group;
      let enrolledCount = 0;
      let pendingScreeningCount = 0;
      for (const patientId of selectedPatientIds) {
        const enrolled = await enrollPatientInGroup({
          medplum,
          group: currentGroup,
          patient: await medplum.readResource('Patient', patientId),
          facilitatorIds,
          facilitatorDisplays,
          organizationId,
          ...(profile?.resourceType === 'Practitioner' && profile.id
            ? {
                requester: {
                  reference: `Practitioner/${profile.id}` as const,
                  display: formatPractitionerPreferredLabel(profile) || undefined,
                },
              }
            : {}),
        });
        currentGroup = enrolled.group;
        await syncAccessAndParticipants({
          action: 'enroll',
          patientId,
          careTeamId: enrolled.careTeam.id,
        });
        enrolledCount += 1;
        if (enrolled.episodeOfCare.status !== 'active') pendingScreeningCount += 1;
      }
      notifications.show({
        color: pendingScreeningCount ? 'yellow' : 'green',
        message:
          (enrolledCount === 1
            ? 'Member enrolled with patient-specific EoC and CareTeam.'
            : `${enrolledCount} members enrolled with patient-specific EoCs and CareTeams.`)
          + (pendingScreeningCount
            ? ` ${pendingScreeningCount === 1 ? 'Their seat is reserved but the episode stays pending until screening is completed' : `${pendingScreeningCount} seats are reserved but those episodes stay pending until screening is completed`} in the episode tracker.`
            : ''),
      });
      closeAdd();
      setSelectedPatientIds([]);
      await load();
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Enrollment failed',
        message: e instanceof Error ? e.message : String(e),
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleEndParticipation = async (
    patientId: string,
    mode: GroupParticipationEndMode,
  ) => {
    if (!groupId) return;
    setBusy(true);
    try {
      if (!group) throw new Error('Group not loaded');
      const eoc = await findExistingGroupEnrollmentEpisodeOfCare(medplum, patientId, groupId, {
        includeFinished: false,
        includeCancelled: false,
      });
      const careTeamId = eoc?.team?.[0]?.reference?.startsWith('CareTeam/')
        ? eoc.team[0].reference.replace('CareTeam/', '')
        : undefined;
      await unenrollPatientFromGroup({ medplum, group, patientId, mode });
      await syncAccessAndParticipants({
        action: mode === 'formal-discharge' ? 'discharge' : 'unenroll',
        patientId,
        careTeamId,
      });
      const messages: Record<GroupParticipationEndMode, string> = {
        suspend: 'Member suspended (On Hold); CareTeam access retained.',
        'pre-start-withdraw': 'Pre-start withdrawal recorded; episode cancelled.',
        'formal-discharge': 'Formal discharge completed; CareTeam inactivated.',
      };
      notifications.show({ color: 'green', message: messages[mode] });
      await load();
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Update failed',
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleReturnToGroup = async (patientId: string) => {
    if (!groupId || !group || !summary) return;
    setBusy(true);
    try {
      const returned = await returnPatientToGroup({
        medplum,
        group,
        patientId,
        facilitatorIds: facilitatorReferencesFromForm(summary),
        facilitatorDisplays: facilitatorReferencesFromForm(summary).map(
          (id) => providers.find((provider) => provider.value === id)?.preferredLabel ?? '',
        ),
        organizationId: summary.organizationId,
      });
      await syncAccessAndParticipants({
        action: 'enroll',
        patientId,
        careTeamId: returned.careTeam.id,
      });
      notifications.show({
        color: 'green',
        message: 'Client returned to the group; episode reactivated.',
      });
      await load();
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Return failed',
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  const rosterColumns: UnifyTableColumn<RosterRow>[] = [
    { key: 'patientName', label: 'Client', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) =>
        row.status === 'onhold' ? (
          <Badge color="yellow" variant="light">
            On Hold
          </Badge>
        ) : (
          <Badge color="green" variant="light">
            Active
          </Badge>
        ),
    },
    {
      key: 'enrolledDate',
      label: 'Enrolled',
      sortable: true,
      render: (row) => row.enrolledDate ?? '—',
    },
    {
      key: 'actions',
      label: '',
      width: 60,
      render: (row) =>
        canManageRoster ? (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                aria-label={`Actions for ${row.patientName}`}
                disabled={busy}
              >
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {row.status === 'onhold' ? (
                <Menu.Item onClick={() => void handleReturnToGroup(row.patientId)}>
                  Return to group
                </Menu.Item>
              ) : (
                <>
                  <Menu.Item onClick={() => void handleEndParticipation(row.patientId, 'suspend')}>
                    Suspend (On Hold)
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => void handleEndParticipation(row.patientId, 'pre-start-withdraw')}
                  >
                    Pre-start withdraw
                  </Menu.Item>
                  <Menu.Item
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => void handleEndParticipation(row.patientId, 'formal-discharge')}
                  >
                    Formal discharge
                  </Menu.Item>
                </>
              )}
            </Menu.Dropdown>
          </Menu>
        ) : null,
    },
  ];

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <MantineGroup justify="center">
          <Loader />
        </MantineGroup>
      </Container>
    );
  }

  if (!summary) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="md">
          <Anchor component={Link} to="/dashboard/groups" size="sm">
            <MantineGroup gap={4}>
              <IconArrowLeft size={14} />
              Back to groups
            </MantineGroup>
          </Anchor>
          <Text c="dimmed">Group not found.</Text>
        </Stack>
      </Container>
    );
  }

  const lifecycleStatus = summary.lifecycleStatus ?? (summary.active ? 'active' : 'inactive');

  return (
    <Container size="lg" py="xl" data-testid="group-detail-page">
      <Stack gap="lg">
        <Anchor component={Link} to="/dashboard/groups" size="sm">
          <MantineGroup gap={4}>
            <IconArrowLeft size={14} />
            Back to groups
          </MantineGroup>
        </Anchor>

        <MantineGroup align="flex-start" gap="md" wrap="nowrap">
          <GroupAvatar imageUrl={summary.imageUrl} groupId={summary.id} size={96} />
          <Stack gap={4} style={{ flex: 1 }}>
            <Title order={2}>{summary.name}</Title>
            <Text size="sm" c="dimmed">
              {summary.organizationName}
              {summary.templateDisplay ? ` · ${summary.templateDisplay}` : ''}
              {summary.programDisplay ? ` · ${summary.programDisplay}` : ''}
            </Text>
            <MantineGroup gap="xs">
              <Badge color={GROUP_LIFECYCLE_STATUS_COLORS[lifecycleStatus]} variant="light">
                {GROUP_LIFECYCLE_STATUS_LABELS[lifecycleStatus]}
              </Badge>
              <Badge color="teal" variant="light">
                Screening required
              </Badge>
              {enrollmentLocked && (
                <Badge color="orange" variant="light">
                  Enrollment locked
                </Badge>
              )}
            </MantineGroup>
          </Stack>
          {canEditSetup && (
            <Button
              variant="default"
              leftSection={<IconPencil size={16} />}
              onClick={openEdit}
              data-testid="edit-group-setup-button"
            >
              Edit group
            </Button>
          )}
        </MantineGroup>

        {enrollmentLocked && (
          <Alert color="orange" title="Enrollment locked">
            This closed group is past its enrollment lock date. Adding members may require an
            override workflow.
          </Alert>
        )}

        <Card withBorder radius="md">
          <MantineGroup justify="space-between" mb="md">
            <div>
              <Title order={4}>Members</Title>
              <Text size="sm" c="dimmed">
                {roster.length} / {summary.maxEnrollment} enrolled — suspend keeps CareTeam access;
                formal discharge closes the episode.
              </Text>
            </div>
            {canManageRoster && (
              <Button
                leftSection={<IconPlus size={16} />}
                disabled={!canAddMember}
                onClick={openAddMembers}
                data-testid="add-group-member-button"
              >
                Add members
              </Button>
            )}
          </MantineGroup>

          {roster.length === 0 ? (
            <Text c="dimmed" size="sm">
              No members enrolled yet.
            </Text>
          ) : (
            <UnifyTable
              data={roster}
              columns={rosterColumns}
              getRowKey={(row) => row.patientId}
              withPaper={false}
              searchable={false}
              pagination={false}
            />
          )}
        </Card>

        <Card withBorder radius="md" data-testid="group-sessions-card">
          <Stack gap="md">
            <MantineGroup justify="space-between" align="center" wrap="wrap">
              <Title order={4}>Cohort sessions</Title>
              {groupId && (
                <Button
                  variant="light"
                  color="gold"
                  leftSection={<IconPlus size={16} />}
                  onClick={() =>
                    navigate(
                      `/dashboard/calendar?groupId=${encodeURIComponent(groupId)}&appointmentCategory=group`,
                    )
                  }
                  data-testid="add-one-off-group-session-button"
                >
                  Add one-off session
                </Button>
              )}
            </MantineGroup>
            {sessions.length === 0 ? (
              <Text c="dimmed" size="sm">
                No sessions found yet. Activate the group to generate the appointment series, or add a
                one-off session from the calendar.
              </Text>
            ) : (
              <ScrollArea h={320} type="auto" offsetScrollbars>
                <Stack gap="xs" pr="xs">
                  {sessions.map((appt) => {
                    const sessionProps = {
                      label: sessionDateLabel(appt.start),
                      description: sessionTimeLabel(appt),
                      leftSection: <IconCalendarEvent size={20} />,
                      rightSection: (
                        <MantineGroup gap="xs" wrap="nowrap">
                          <Badge color={appt.status === 'booked' ? 'blue' : 'gray'} variant="light">
                            {appt.status ?? 'Unknown'}
                          </Badge>
                          {appt.id ? <IconChevronRight size={16} /> : null}
                        </MantineGroup>
                      ),
                      color: 'gold' as const,
                      variant: 'subtle' as const,
                      style: { borderRadius: 'var(--mantine-radius-md)' },
                    };
                    return appt.id ? (
                      <NavLink
                        key={appt.id}
                        component={Link}
                        to={`/dashboard/appointments/${appt.id}`}
                        {...sessionProps}
                      />
                    ) : (
                      <NavLink key={appt.start ?? 'unscheduled'} {...sessionProps} />
                    );
                  })}
                </Stack>
              </ScrollArea>
            )}
          </Stack>
        </Card>

        {summary.enrollmentMode === 'closed' && summary.templateReference && (
          <Card withBorder radius="md" data-testid="group-waitlist-panel">
            <MantineGroup justify="space-between" align="center" wrap="wrap">
              <Title order={4}>Program waitlist</Title>
              <Button
                variant="light"
                onClick={() => navigate('/dashboard/episode-tracker?tab=waitlist')}
              >
                Open waitlist tracker
              </Button>
            </MantineGroup>
          </Card>
        )}
      </Stack>

      <Modal opened={addOpened} onClose={closeAdd} title="Add group members" centered>
        <Stack gap="md">
          <MultiSelect
            label="Clients"
            placeholder={
              remainingCapacity > 0
                ? `Select up to ${remainingCapacity} client${remainingCapacity === 1 ? '' : 's'}`
                : 'Group is at capacity'
            }
            searchable
            data={patientOptions}
            value={selectedPatientIds}
            onChange={(ids) => setSelectedPatientIds(ids.slice(0, remainingCapacity))}
            maxValues={remainingCapacity || undefined}
            data-testid="add-member-patient-select"
          />
          <Text size="xs" c="dimmed">
            {remainingCapacity} seat{remainingCapacity === 1 ? '' : 's'} remaining
            {selectedPatientIds.length > 0
              ? ` · ${selectedPatientIds.length} selected`
              : ''}
          </Text>
          <MantineGroup justify="flex-end">
            <Button variant="default" onClick={closeAdd}>
              Cancel
            </Button>
            <Button
              loading={busy}
              disabled={selectedPatientIds.length === 0}
              onClick={() => void handleAddMembers()}
              data-testid="confirm-add-member-button"
            >
              {selectedPatientIds.length > 1
                ? `Add ${selectedPatientIds.length} members`
                : 'Add member'}
            </Button>
          </MantineGroup>
        </Stack>
      </Modal>

      <GroupSetupFormModal
        opened={editOpened}
        title="Edit group"
        initialValues={summary}
        templates={templates}
        providers={providers}
        wasActive={summary.active}
        saving={savingSetup}
        onClose={closeEdit}
        onSubmit={async (values, activate) => {
          setSavingSetup(true);
          try {
            await saveGroup({ ...values, active: activate }, summary.id);
            notifications.show({
              color: 'green',
              message: activate ? 'Group saved and sessions generated.' : 'Group saved.',
            });
            closeEdit();
            await load();
          } catch (e) {
            notifications.show({
              color: 'red',
              title: 'Save failed',
              message: e instanceof Error ? e.message : String(e),
            });
          } finally {
            setSavingSetup(false);
          }
        }}
      />
    </Container>
  );
}
